import { useState } from "react";
import { ArrowDown, ArrowUp, Copy, GripVertical, Pencil, Plus, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { confirmAction } from "@/components/app/Confirm";
import { newQuestion } from "@/lib/library";
import { duplicate, issues, isScored, itemText, kindLabel } from "@/lib/questionRules";
import { newScenario, AiDraft } from "@/pages/assessments/builder/ScenarioEditor";
import { QuestionDrawer } from "@/pages/assessments/builder/QuestionDrawer";

const ADD = { quiz: [["single", "Multiple choice"], ["short", "Short answer"]], custom: [["yesno", "Yes / No"], ["single", "Multiple choice"], ["short", "Short text"], ["text", "Long text"]] };

function AddButton({ secType, onAdd, testId }) {
  if (secType === "scenarios") return <Button size="sm" onClick={() => onAdd()} data-testid={testId} data-tour="add-question"><Plus size={14} /> Add scenario</Button>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" data-testid={testId} data-tour="add-question"><Plus size={14} /> Add question</Button></DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {ADD[secType].map(([k, l]) => <DropdownMenuItem key={k} onSelect={() => onAdd(k)} data-testid={`${testId}-${k}`}>{l}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Row({ item, i, n, secType, canEdit, onOpen, onMove, onDup, onDel, drag }) {
  const bad = Object.keys(issues(item, secType)).length > 0;
  const pts = secType === "scenarios" ? "Ranked" : isScored(item, secType) ? `${item.points} pt` : "Info only";
  return (
    <li draggable={canEdit} {...drag} className="group flex items-start gap-3 rounded-[6px] border border-line bg-white px-3 py-3 transition-colors hover:bg-surface-2/60" data-testid={`item-row-${i}`}>
      {canEdit && <GripVertical size={16} className="mt-1 shrink-0 cursor-grab text-ink-muted" aria-hidden />}
      <span className="mt-0.5 w-6 shrink-0 text-right font-display text-sm font-semibold tabular-nums">{i + 1}</span>
      <button className="min-w-0 flex-1 space-y-1.5 text-left" onClick={onOpen} data-testid={`item-open-${i}`}>
        <p className="line-clamp-2 text-sm">{itemText(item, secType) || <span className="text-ink-muted">(Not written yet)</span>}</p>
        <span className="flex flex-wrap gap-1.5 text-[11px]">
          <span className="rounded-full bg-surface-2 px-2 py-0.5">{kindLabel(item, secType)}</span>
          {item.competency && <span className="rounded-full bg-brand-tint px-2 py-0.5 text-brand">{item.competency}</span>}
          <span className="rounded-full border border-line px-2 py-0.5 text-ink-secondary">{pts}</span>
          {item.ai_draft && <span className="rounded-full bg-caution-tint px-2 py-0.5 text-caution" data-testid={`item-ai-${i}`}>AI draft · review</span>}
        </span>
      </button>
      <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${bad ? "bg-risk" : "bg-success"}`} title={bad ? "Incomplete" : "Complete"} aria-label={bad ? "Incomplete" : "Complete"} data-testid={`item-dot-${i}`} />
      <span className="flex shrink-0 gap-0.5">
        <Button size="icon" variant="ghost" onClick={onOpen} aria-label={`Edit ${i + 1}`} data-testid={`item-edit-${i}`}><Pencil size={14} /></Button>
        {canEdit && <>
          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => onMove(-1)} aria-label={`Move ${i + 1} up`} data-testid={`item-up-${i}`}><ArrowUp size={14} /></Button>
          <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => onMove(1)} aria-label={`Move ${i + 1} down`} data-testid={`item-down-${i}`}><ArrowDown size={14} /></Button>
          <Button size="icon" variant="ghost" onClick={onDup} aria-label={`Duplicate ${i + 1}`} data-testid={`item-dup-${i}`}><Copy size={14} /></Button>
          <Button size="icon" variant="ghost" onClick={onDel} aria-label={`Delete ${i + 1}`} data-testid={`item-del-${i}`}><Trash2 size={14} /></Button>
        </>}
      </span>
    </li>
  );
}

export function ItemSection({ s, onChange, canEdit, templateId, jobId, autosave }) {
  const key = s.type === "scenarios" ? "scenarios" : "questions";
  const items = s[key];
  const [open, setOpen] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const set = (list) => onChange({ ...s, [key]: list });
  const move = (from, to) => { if (to < 0 || to >= items.length) return; const n = [...items]; const [x] = n.splice(from, 1); n.splice(to, 0, x); set(n); };
  const add = (kind) => { const it = s.type === "scenarios" ? newScenario() : newQuestion(kind); set([...items, it]); setOpen(items.length); };
  const del = async (i) => {
    if (!(await confirmAction({ title: "Delete this question?", body: "You can undo right after.", entity: itemText(items[i], s.type)?.slice(0, 60) || `Question ${i + 1}`, confirmLabel: "Delete" }))) return;
    const before = items;
    set(items.filter((_, j) => j !== i));
    toast("Question deleted", { action: { label: "Undo", onClick: () => set(before) } });
  };
  return (
    <div className="space-y-3" data-testid={`item-section-${s.type}`}>
      {canEdit && <div className="flex flex-wrap items-center gap-2">
        <AddButton secType={s.type} onAdd={add} testId={`add-item-top-${s.type}`} />
        {s.type === "scenarios" && <AiDraft templateId={templateId} jobId={jobId} onDrafted={(x) => set([...items, ...x.map((y) => ({ ...y, ai_draft: true }))])} />}
      </div>}
      {!items.length ? <p className="rounded-[6px] border border-dashed border-line p-4 text-sm text-ink-muted">No {s.type === "scenarios" ? "scenarios" : "questions"} yet.</p> : (
        <ol className="space-y-2" data-testid={`item-list-${s.type}`} data-tour="question-list">
          {items.map((it, i) => (
            <Row key={it.id} item={it} i={i} n={items.length} secType={s.type} canEdit={canEdit} onOpen={() => setOpen(i)} onMove={(d) => move(i, i + d)}
              onDup={() => { const n = [...items]; n.splice(i + 1, 0, duplicate(it)); set(n); }} onDel={() => del(i)}
              drag={{ onDragStart: () => setDragFrom(i), onDragOver: (e) => e.preventDefault(), onDrop: () => { move(dragFrom, i); setDragFrom(null); } }} />
          ))}
        </ol>
      )}
      {canEdit && items.length > 2 && <AddButton secType={s.type} onAdd={add} testId={`add-item-bottom-${s.type}`} />}
      {items.some((x) => x.ai_draft) && <p className="flex items-center gap-1 text-xs text-caution"><Info size={13} /> AI drafts are marked until you open and review them.</p>}
      <QuestionDrawer section={s} index={open} onIndex={setOpen} canEdit={canEdit} autosave={autosave} onClose={() => setOpen(null)}
        onChangeItem={(i, x) => set(items.map((y, j) => (j === i ? x : y)))} onAddAnother={() => add(s.type === "quiz" ? "single" : s.type === "custom" ? "text" : undefined)} />
    </div>
  );
}
